import { EDITOR_ACTIVE_PAGE_FOCUS_ID, PAGE_CONTAINER_CLASSNAME } from 'modules/Editor/Editor.config';
import { selectIsViewOnly, selectZoom } from 'modules/Editor/store/editorSettingsSelector';
import { setZoom } from 'modules/Editor/store/editorSettingsSlice';
import { useAppDispatch, useAppSelector } from 'modules/Editor/store/hooks';
import {
  selectAllPageBackgroundData,
  selectInfographHeightPx,
  selectInfographWidthPx,
  selectPageBackgroundString,
} from 'modules/Editor/store/infographSelector';
import { selectActivePage } from 'modules/Editor/store/pageSelector';
import { Page } from 'modules/common/components/Page';
import { ReactElement, ReactNode, memo, useEffect, useRef } from 'react';

import { useFocus } from '../Focus';
import {
  adjustPageZoom,
  getCurrentScrollRatios,
  getFitToScreenScale,
  isLongPortraitLayout,
  scrollToFocusPosition,
} from '../PageControls/ZoomControl.helpers';
import { getPaperType } from '../Toolbar/PageMenu/EditPageSize/helpers';
import { GridOverlay } from './GridOverlay';
import { MarginOverlay } from './MarginOverlay';

export const PageContainer = memo(({ children }: { children: ReactNode }): ReactElement => {
  const activePageId = useAppSelector(selectActivePage);
  const widthPx = useAppSelector(selectInfographWidthPx);
  const heightPx = useAppSelector(selectInfographHeightPx);
  const pageBackgroundString = useAppSelector(selectPageBackgroundString(activePageId));
  const pageBackgroundData = useAppSelector(selectAllPageBackgroundData(activePageId));
  const zoom = useAppSelector(selectZoom);
  const isViewOnly = useAppSelector(selectIsViewOnly);
  const tabIndexAttribute = isViewOnly ? {} : { tabIndex: 0 };
  const dispatch = useAppDispatch();

  const { pageScrollContainerRef, activePageRef, pageSizeAreaRef, pageScaleAreaRef, pageFocusWrapperRef } = useFocus();
  const prevWidthRef = useRef(widthPx);
  const prevHeightRef = useRef(heightPx);

  const onChangeFitToPage = () => {
    const pageScrollEl = pageScrollContainerRef?.current;
    const currentScrollRatios = getCurrentScrollRatios(pageScrollContainerRef, pageFocusWrapperRef);
    const fitToPageZoomVal = getFitToScreenScale(pageScrollContainerRef?.current!, true);
    adjustPageZoom(fitToPageZoomVal, pageSizeAreaRef?.current, pageScaleAreaRef?.current);

    // scroll page to top if page was resized for long portrait layouts
    if (isLongPortraitLayout()) {
      pageScrollEl!.scrollTop = 0;
    } else {
      scrollToFocusPosition(pageScrollContainerRef, pageFocusWrapperRef, currentScrollRatios);
    }
    dispatch(setZoom(fitToPageZoomVal));
  };

  // This effect runs when either widthPx or heightPx changes.
  useEffect(() => {
    const isDefaultPresetPaper = getPaperType({ widthPx, heightPx }) !== undefined;
    // if its default preset paper, will fit to page
    if (isDefaultPresetPaper) onChangeFitToPage();

    // if there is a change in custom page size, fit to page
    if (widthPx !== prevWidthRef.current || heightPx !== prevHeightRef.current) {
      onChangeFitToPage();
      prevWidthRef.current = widthPx;
      prevHeightRef.current = heightPx;
    }
    // for this effect is only require to observe whenever there's a change in the page component's width or height
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widthPx, heightPx]);

  return (
    <Page
      testId={activePageId}
      className={PAGE_CONTAINER_CLASSNAME}
      bg={pageBackgroundString}
      width={widthPx}
      height={heightPx}
      zoom={zoom}
      focusElementId={EDITOR_ACTIVE_PAGE_FOCUS_ID}
      focusElementTestId='page-focus-element'
      pageRef={activePageRef}
      pageSizeRef={pageSizeAreaRef}
      pageScaleRef={pageScaleAreaRef}
      {...tabIndexAttribute}
    >
      <MarginOverlay />
      <GridOverlay pageBackground={pageBackgroundData} hasLargeLineSize={zoom < 0.5} />
      {children}
    </Page>
  );
});
PageContainer.displayName = 'PageContainer';
